import React from "react";
import config from "../config";

export default function Privacy() {
  const { company } = config;

  return (
    <div className="pt-[72px]">
      {/* Header */}
      <section className="border-b border-white/5 bg-slate-950 py-16">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h1 className="text-4xl font-extrabold text-white">
            Privacy Policy
          </h1>
          <p className="mt-3 text-slate-400">
            Your privacy is important to us. This policy explains how we handle
            your information.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          <p className="mb-10 text-sm text-slate-500">
            Last updated: January 2025
          </p>

          <div className="space-y-10 text-slate-300">
            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                1. Information We Collect
              </h2>
              <p className="leading-relaxed">
                We collect information that you voluntarily provide to us when
                you use our contact form or communicate with us. This includes:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-400">
                <li>
                  <strong className="text-slate-300">Name</strong> — to address
                  you properly in our communications.
                </li>
                <li>
                  <strong className="text-slate-300">Email address</strong> — to
                  respond to your inquiries and communicate about projects.
                </li>
                <li>
                  <strong className="text-slate-300">Message content</strong> —
                  the details of your inquiry, project requirements, or
                  feedback.
                </li>
                <li>
                  <strong className="text-slate-300">Subject</strong> — to
                  categorize and prioritize your inquiry.
                </li>
              </ul>
              <p className="mt-3 leading-relaxed">
                We do not collect personal information automatically. We do not
                use tracking pixels, analytics platforms, or other passive data
                collection methods beyond what is strictly necessary for website
                functionality.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                2. How We Use Your Information
              </h2>
              <p className="leading-relaxed">
                The information we collect is used exclusively for the following
                purposes:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-400">
                <li>
                  Responding to your inquiries and providing requested
                  information about our services.
                </li>
                <li>
                  Communicating with you about potential or ongoing projects.
                </li>
                <li>
                  Sending project proposals, estimates, and invoices when
                  applicable.
                </li>
                <li>Improving our services based on feedback received.</li>
              </ul>
              <p className="mt-3 leading-relaxed">
                We do not sell, rent, or share your personal information with
                third parties for marketing purposes. Your data is never used
                for unsolicited communications.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                3. Data Storage and Security
              </h2>
              <p className="leading-relaxed">
                We take reasonable measures to protect your personal information
                from unauthorized access, alteration, disclosure, or
                destruction. These measures include:
              </p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-400">
                <li>Secure transmission of form data over HTTPS.</li>
                <li>
                  Limited access to personal data on a need-to-know basis.
                </li>
                <li>
                  Regular review of our data collection, storage, and processing
                  practices.
                </li>
              </ul>
              <p className="mt-3 leading-relaxed">
                While we strive to protect your information, no method of
                electronic transmission or storage is 100% secure. We cannot
                guarantee absolute security of your data.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                4. Third-Party Services
              </h2>
              <p className="leading-relaxed">
                We may use third-party SMTP service providers to deliver emails
                submitted through our contact form. These providers process your
                data solely for the purpose of email delivery and are bound by
                their own privacy policies and data protection obligations.
              </p>
              <p className="mt-3 leading-relaxed">
                We do not integrate with social media tracking, advertising
                networks, or third-party analytics platforms. Any third-party
                services we use are limited to essential operational needs.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                5. Cookies
              </h2>
              <p className="leading-relaxed">
                Our website uses minimal or no cookies. We do not use cookies for
                tracking, advertising, or analytics purposes. If any cookies are
                used, they are strictly necessary for website functionality (such
                as session management) and do not collect personal information.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                6. Your Rights
              </h2>
              <p className="leading-relaxed">You have the right to:</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5 text-slate-400">
                <li>
                  <strong className="text-slate-300">Access</strong> — request a
                  copy of any personal data we hold about you.
                </li>
                <li>
                  <strong className="text-slate-300">Correction</strong> —
                  request corrections to inaccurate or incomplete personal data.
                </li>
                <li>
                  <strong className="text-slate-300">Deletion</strong> — request
                  deletion of your personal data from our records.
                </li>
                <li>
                  <strong className="text-slate-300">Objection</strong> — object
                  to the processing of your personal data.
                </li>
              </ul>
              <p className="mt-3 leading-relaxed">
                To exercise any of these rights, please contact us at{" "}
                <a
                  href={`mailto:${company.email}`}
                  className="text-cyan-400 hover:text-cyan-300"
                >
                  {company.email}
                </a>
                . We will respond to your request within a reasonable timeframe.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                7. Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time to reflect
                changes in our practices or for other operational, legal, or
                regulatory reasons. Any changes will be posted on this page with
                an updated revision date.
              </p>
              <p className="mt-3 leading-relaxed">
                We encourage you to review this Privacy Policy periodically to
                stay informed about how we are protecting your information.
                Continued use of our website after any changes constitutes
                acceptance of the updated policy.
              </p>
            </div>

            <div>
              <h2 className="mb-3 text-xl font-semibold text-white">
                8. Contact Information
              </h2>
              <p className="leading-relaxed">
                If you have any questions or concerns about this Privacy Policy
                or our data practices, please contact us at{" "}
                <a
                  href={`mailto:${company.email}`}
                  className="text-cyan-400 hover:text-cyan-300"
                >
                  {company.email}
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
